import axios from 'axios';
import { IHttpClient } from './interfaces/IHttpClient';
import { IHttpClientRequestParameters } from './interfaces/IHttpClientRequestParameters';
import { EnhancedStore } from '@reduxjs/toolkit';
import { reset } from '../slices/userSlice';
import history from '../common/history';
import { BaseResponse } from './responses/baseResponse';
import ValidateToken from './tokenValidator';

let _store: EnhancedStore;

const client = axios.create({
  baseURL: process.env.REACT_APP_PTS_API,
});

export const SetApiClientStore = (store: EnhancedStore) => {
  _store = store;
};

client.interceptors.request.use((config) => {
  return config;
});

client.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response.status === 400) {
      // Non-Model Validation Handling
      if (error.response.data.Message) {
        return Promise.reject(error.response.data.Message);
      }

      // Model Validation Handling
      const errorsHtml = Object.entries(error.response.data.errors)
        .map(
          ([k, v]) =>
            `<ul class='list-disc ml-7'>${Object.values(v as string[])
              .map((x) => `<li>${x}</li>`)
              .join('')}</ul></div>`
        )
        .join('');

      const html = `<div><b class='mb-3 block'>The following validation errors occurred:</b>${errorsHtml}`;

      return Promise.reject(html);
    }

    if (error.response.status === 401) {
      _store.dispatch(reset());
      history.push('/login');
    }

    if (!error.response.data.Message) {
      return Promise.reject(
        'We cannot complete your request at this time, please try again.'
      );
    }

    return Promise.reject(error.response.data.Message);
  }
);

export class HttpClient implements IHttpClient {
  download(
    params: IHttpClientRequestParameters
  ){
    ValidateToken(params, _store).then((p) => {
      const { url } = p;
      client.get(url, {
        method: 'GET',
        responseType: 'blob',
      }).then((response) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(new Blob([response.data]));;
        link.setAttribute('download', `${Date.now()}.xlsx`);
        document.body.appendChild(link);
        link.click();
      });
    });
  }

  downloadAwaitable(
    params: IHttpClientRequestParameters
  ): Promise<void | BaseResponse> {
    return ValidateToken(params, _store).then((p) => {
      const { url } = p;
      return client.get(url, {
        method: 'GET',
        responseType: 'blob',
      }).then((response) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(new Blob([response.data]));;
        link.setAttribute('download', `${Date.now()}.xlsx`);
        document.body.appendChild(link);
        link.click();
      })
    });
  }

  get<TResponse extends BaseResponse>(
    params: IHttpClientRequestParameters
  ): Promise<TResponse> {
    return ValidateToken(params, _store).then((p) => {
      const { url, headers } = p;
      return client.get(url, { headers: headers });
    });
  }

  post<TResponse extends BaseResponse>(
    params: IHttpClientRequestParameters
  ): Promise<TResponse> {
    return ValidateToken(params, _store).then((p) => {
      const { url, payload, headers } = p;
      return client.post(url, payload, { headers: headers });
    });
  }

  put<TResponse extends BaseResponse>(
    params: IHttpClientRequestParameters
  ): Promise<TResponse> {
    return ValidateToken(params, _store).then((p) => {
      const { url, payload, headers } = p;
      return client.put(url, payload, { headers: headers });
    });
  }

  delete<TResponse extends BaseResponse>(
    params: IHttpClientRequestParameters
  ): Promise<TResponse> {
    return ValidateToken(params, _store).then((p) => {
      const { url, headers } = p;
      return client.delete(url, { headers: headers });
    });
  }
}
